// Real Shopify API Integration Service
class RealShopifyService {
  constructor() {
    this.shopDomain = process.env.REACT_APP_SHOPIFY_SHOP_DOMAIN;
    this.accessToken = process.env.REACT_APP_SHOPIFY_ACCESS_TOKEN;
    this.apiVersion = '2024-01';
    this.baseURL = `https://${this.shopDomain}.myshopify.com/admin/api/${this.apiVersion}`;
  }

  // Get products from Shopify
  async getProducts(limit = 50, page = 1) {
    try {
      const response = await fetch(`${this.baseURL}/products.json?limit=${limit}&page=${page}`, {
        method: 'GET',
        headers: {
          'X-Shopify-Access-Token': this.accessToken,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Shopify API error: ${response.status}`);
      }

      const data = await response.json();
      return this.parseShopifyProducts(data.products);
    } catch (error) {
      console.error('Error getting Shopify products:', error);
      // Fallback to demo products
      return this.getDemoProducts();
    }
  }

  // Parse Shopify products
  parseShopifyProducts(products) {
    return products.map(product => ({
      id: product.id,
      title: product.title,
      description: product.body_html,
      price: parseFloat(product.variants[0]?.price || 0),
      currency: 'USD',
      images: product.images.map(img => img.src),
      variants: product.variants.map(variant => ({
        id: variant.id,
        title: variant.title,
        price: parseFloat(variant.price),
        inventory: variant.inventory_quantity,
        sku: variant.sku
      })),
      tags: product.tags.split(',').map(tag => tag.trim()),
      vendor: product.vendor,
      productType: product.product_type,
      status: product.status,
      createdAt: product.created_at,
      updatedAt: product.updated_at
    }));
  }

  // Get product by ID
  async getProduct(productId) {
    try {
      const response = await fetch(`${this.baseURL}/products/${productId}.json`, {
        method: 'GET',
        headers: {
          'X-Shopify-Access-Token': this.accessToken,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Shopify API error: ${response.status}`);
      }

      const data = await response.json();
      return this.parseShopifyProducts([data.product])[0];
    } catch (error) {
      console.error('Error getting Shopify product:', error);
      throw error;
    }
  }

  // Create order in Shopify
  async createOrder(orderData) {
    try {
      const order = {
        order: {
          line_items: orderData.items.map(item => ({
            variant_id: item.variantId,
            quantity: item.quantity
          })),
          customer: {
            first_name: orderData.customer.firstName,
            last_name: orderData.customer.lastName,
            email: orderData.customer.email,
            phone: orderData.customer.phone
          },
          shipping_address: {
            first_name: orderData.shipping.firstName,
            last_name: orderData.shipping.lastName,
            address1: orderData.shipping.address,
            city: orderData.shipping.city,
            province: orderData.shipping.state,
            zip: orderData.shipping.zipCode,
            country: orderData.shipping.country || 'MX',
            phone: orderData.shipping.phone
          },
          billing_address: {
            first_name: orderData.billing.firstName,
            last_name: orderData.billing.lastName,
            address1: orderData.billing.address,
            city: orderData.billing.city,
            province: orderData.billing.state,
            zip: orderData.billing.zipCode,
            country: orderData.billing.country || 'MX',
            phone: orderData.billing.phone
          },
          financial_status: 'paid',
          fulfillment_status: 'unfulfilled',
          tags: 'silva-logistics',
          note: orderData.notes || ''
        }
      };

      const response = await fetch(`${this.baseURL}/orders.json`, {
        method: 'POST',
        headers: {
          'X-Shopify-Access-Token': this.accessToken,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(order)
      });

      if (!response.ok) {
        throw new Error(`Shopify API error: ${response.status}`);
      }

      const data = await response.json();
      return this.parseShopifyOrder(data.order);
    } catch (error) {
      console.error('Error creating Shopify order:', error);
      throw error;
    }
  }

  // Parse Shopify order
  parseShopifyOrder(order) {
    return {
      id: order.id,
      orderNumber: order.order_number,
      name: order.name,
      totalPrice: parseFloat(order.total_price),
      currency: order.currency,
      financialStatus: order.financial_status,
      fulfillmentStatus: order.fulfillment_status,
      customer: {
        id: order.customer?.id,
        firstName: order.customer?.first_name,
        lastName: order.customer?.last_name,
        email: order.customer?.email
      },
      shippingAddress: {
        firstName: order.shipping_address?.first_name,
        lastName: order.shipping_address?.last_name,
        address: order.shipping_address?.address1,
        city: order.shipping_address?.city,
        state: order.shipping_address?.province,
        zipCode: order.shipping_address?.zip,
        country: order.shipping_address?.country
      },
      items: order.line_items.map(item => ({
        id: item.id,
        productId: item.product_id,
        variantId: item.variant_id,
        title: item.title,
        quantity: item.quantity,
        price: parseFloat(item.price)
      })),
      createdAt: order.created_at,
      updatedAt: order.updated_at
    };
  }

  // Get orders from Shopify
  async getOrders(limit = 50, status = null) {
    try {
      let url = `${this.baseURL}/orders.json?limit=${limit}`;
      if (status) {
        url += `&status=${status}`;
      }

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'X-Shopify-Access-Token': this.accessToken,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Shopify API error: ${response.status}`);
      }

      const data = await response.json();
      return data.orders.map(order => this.parseShopifyOrder(order));
    } catch (error) {
      console.error('Error getting Shopify orders:', error);
      throw error;
    }
  }

  // Update order status
  async updateOrderStatus(orderId, status) {
    try {
      const response = await fetch(`${this.baseURL}/orders/${orderId}.json`, {
        method: 'PUT',
        headers: {
          'X-Shopify-Access-Token': this.accessToken,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          order: {
            id: orderId,
            fulfillment_status: status
          }
        })
      });

      if (!response.ok) {
        throw new Error(`Shopify API error: ${response.status}`);
      }

      const data = await response.json();
      return this.parseShopifyOrder(data.order);
    } catch (error) {
      console.error('Error updating Shopify order:', error);
      throw error;
    }
  }

  // Create fulfillment
  async createFulfillment(orderId, trackingInfo) {
    try {
      const fulfillment = {
        fulfillment: {
          order_id: orderId,
          tracking_number: trackingInfo.trackingNumber,
          tracking_company: trackingInfo.carrier,
          tracking_url: trackingInfo.trackingUrl,
          notify_customer: true
        }
      };

      const response = await fetch(`${this.baseURL}/orders/${orderId}/fulfillments.json`, {
        method: 'POST',
        headers: {
          'X-Shopify-Access-Token': this.accessToken,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(fulfillment)
      });

      if (!response.ok) {
        throw new Error(`Shopify API error: ${response.status}`);
      }

      const data = await response.json();
      return data.fulfillment;
    } catch (error) {
      console.error('Error creating Shopify fulfillment:', error);
      throw error;
    }
  }

  // Get customers
  async getCustomers(limit = 50) {
    try {
      const response = await fetch(`${this.baseURL}/customers.json?limit=${limit}`, {
        method: 'GET',
        headers: {
          'X-Shopify-Access-Token': this.accessToken,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Shopify API error: ${response.status}`);
      }

      const data = await response.json();
      return data.customers.map(customer => ({
        id: customer.id,
        firstName: customer.first_name,
        lastName: customer.last_name,
        email: customer.email,
        phone: customer.phone,
        totalSpent: parseFloat(customer.total_spent),
        ordersCount: customer.orders_count,
        createdAt: customer.created_at,
        updatedAt: customer.updated_at
      }));
    } catch (error) {
      console.error('Error getting Shopify customers:', error);
      throw error;
    }
  }

  // Get inventory levels
  async getInventoryLevels(locationId) {
    try {
      const response = await fetch(`${this.baseURL}/inventory_levels.json?location_ids=${locationId}`, {
        method: 'GET',
        headers: {
          'X-Shopify-Access-Token': this.accessToken,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Shopify API error: ${response.status}`);
      }

      const data = await response.json();
      return data.inventory_levels.map(level => ({
        inventoryItemId: level.inventory_item_id,
        locationId: level.location_id,
        available: level.available,
        updatedAt: level.updated_at
      }));
    } catch (error) {
      console.error('Error getting Shopify inventory levels:', error);
      throw error;
    }
  }

  // Demo products fallback
  getDemoProducts() {
    return [
      {
        id: 1,
        title: 'Producto Demo 1',
        description: 'Descripción del producto demo 1',
        price: 99.99,
        currency: 'USD',
        images: ['https://via.placeholder.com/300x300'],
        variants: [{
          id: 1,
          title: 'Default',
          price: 99.99,
          inventory: 10,
          sku: 'DEMO-001'
        }],
        tags: ['demo', 'producto'],
        vendor: 'Silva Logistics',
        productType: 'demo',
        status: 'active',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      },
      {
        id: 2,
        title: 'Producto Demo 2',
        description: 'Descripción del producto demo 2',
        price: 149.99,
        currency: 'USD',
        images: ['https://via.placeholder.com/300x300'],
        variants: [{
          id: 2,
          title: 'Default',
          price: 149.99,
          inventory: 5,
          sku: 'DEMO-002'
        }],
        tags: ['demo', 'producto'],
        vendor: 'Silva Logistics',
        productType: 'demo',
        status: 'active',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      }
    ];
  }
}

// Create singleton instance
const realShopifyService = new RealShopifyService();

export default realShopifyService; 